import { select, scaleOrdinal, arc, pie } from 'd3';
import { OmniElement, OmniStyleElement, html, css } from 'omni-ui';

OmniStyleElement.register();

export class CampaignDonutChart extends OmniElement {
  static get properties() {
    return {
      defaultValue: { type: String },
      placeholder: { type: String },
      subtitle: { type: String },
      percentage: { type: String },
      strategicData: { type: Object },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        polyline {
          opacity: 0.3;
          stroke: black;
          stroke-width: 0.143rem;
          fill: none;
          shape-rendering: geometricPrecision;
        }

        svg {
          min-width: 32.143rem;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.duration = 1000;
  }

  firstUpdated() {
    this._initializeChart();
  }

  updated() {
    this._updateChart();
  }

  // get object of subtitle: percentage key value pairs to be used in donut chart as data
  _getReducedData = () =>
    Object.values(this.strategicData || {}).reduce(
      (acc, { subtitle, percentage }) => {
        // eslint-disable-next-line no-param-reassign
        acc[subtitle] = percentage;
        return acc;
      },
      {},
    );

  // determine outer edge of pie chart
  _lineHelper = ([x1, y1], [x2, y2], ratio) => [
    x1 * ratio + x2 * (1 - ratio),
    y1 * ratio + y2 * (1 - ratio),
  ];

  // construct svg box and general properties for donut chart
  _initializeChart() {
    const [width, height] = [730, 617];

    const data = this._getReducedData();

    // The radius of the pieplot is half the width or half the height (max)
    this.radius = Math.max(width, height) / 2;

    // div that svg is appended to
    this.element = document.createElement('div');

    // append the svg object to a div
    this.donutChart = select(this.element)
      .append('svg')
      .attr('width', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // set the color scale
    this.color = scaleOrdinal()
      .domain(Object.keys(data))
      .range([
        'rgba(var(--rgb-primary), 0.8)',
        'rgba(var(--rgb-primary), 0.6)',
        'rgba(var(--rgb-primary), 0.4)',
        'rgba(var(--rgb-primary), 0.2)',
        'rgba(var(--rgb-primary))',
      ]);

    // generate the inside arc
    this.arc = arc()
      .innerRadius(this.radius * 0.5) // This is the size of the donut hole
      .outerRadius(this.radius * 0.72);

    // generate arc for labels and polylines
    this.outerarc = arc()
      .innerRadius(this.radius * 0.8)
      .outerRadius(this.radius * 0.8);

    const container = this.shadowRoot.querySelector('omni-style');

    container.appendChild(this.element);
  }

  // controls construction of the pie chart itself
  _updateChart() {
    // Compute the position of each group on the pie:
    const pieChart = pie()
      .sort(null) // Do not sort slices by size so that slices remain static
      .value(d => d[1])
      .padAngle(0.1); // sets spacing beteen pie pieces

    // creates array to be used as pie data
    this.donutData = pieChart(Object.entries(this._getReducedData()));

    // create initial slices
    const slices = this.donutChart
      .selectAll('path')
      .data(this.donutData, d => d.index);

    // first render of slices with fade in transition
    slices
      .enter()
      .append('path')
      .attr('d', this.arc)
      .attr('opacity', 0)
      // set color based on color array not based on data value
      .attr('fill', (d, i) => this.color(i))
      .transition()
      .duration(this.duration)
      .attr('opacity', 1);

    // rerender slices on update with transition
    slices
      .transition()
      .duration(this.duration)
      .attr('d', this.arc)
      // set color based on color array not based on data value
      .attr('fill', (_d, i) => this.color(i));

    slices.exit().remove();

    // Add the polylines between chart and labels
    const lines = this.donutChart.selectAll('polyline').data(this.donutData);

    // first render of lines
    lines
      .enter()
      .append('polyline')
      // create a triangle between centr of pie chart, center of outer hidden ring, and label position
      .attr('points', d => [
        this._lineHelper(this.arc.centroid(d), this.outerarc.centroid(d), 0.4),
        this.outerarc.centroid(d),
        this.outerarc.centroid(d),
      ])
      .transition()
      .duration(this.duration)
      .attr('points', d => {
        // create a triangle between centr of pie chart, center of outer hidden ring, and label position
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        const pos = this.outerarc.centroid(d);
        pos[0] = this.radius * 0.78 * (midangle < Math.PI ? 1 : -1);
        return [
          this._lineHelper(
            this.arc.centroid(d),
            this.outerarc.centroid(d),
            0.4,
          ),
          this.outerarc.centroid(d),
          pos,
        ];
      });

    // rerender lines on update with transition
    lines
      .transition()
      .duration(this.duration)
      .attr('points', d => {
        // create a triangle between centr of pie chart, center of outer hidden ring, and label position
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        const pos = this.outerarc.centroid(d);
        pos[0] = this.radius * 0.78 * (midangle < Math.PI ? 1 : -1);
        return [
          this._lineHelper(
            this.arc.centroid(d),
            this.outerarc.centroid(d),
            0.4,
          ),
          this.outerarc.centroid(d),
          pos,
        ];
      });

    lines.exit().remove();

    // Add the labels:
    const labels = this.donutChart.selectAll('text').data(this.donutData);

    // first render of labels
    labels
      .enter()
      .append('text')
      .text(d => d.data[0])
      .style('text-anchor', d => {
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midangle < Math.PI ? 'start' : 'end';
      })
      .attr('transform', d => {
        const pos = this.outerarc.centroid(d);
        return `translate(${pos})`;
      })
      .transition()
      .duration(this.duration)
      .attr('transform', d => {
        const pos = this.outerarc.centroid(d);
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = this.radius * 0.8 * (midangle < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      });

    // rerender labels on update with transition
    labels
      .text(d => d.data[0])
      .style('text-anchor', d => {
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midangle < Math.PI ? 'start' : 'end';
      })
      .transition()
      .duration(this.duration)
      .attr('transform', d => {
        const pos = this.outerarc.centroid(d);
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = this.radius * 0.8 * (midangle < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      });

    labels.exit().remove();
  }

  render() {
    return html` <omni-style></omni-style> `;
  }
}

OmniElement.register('campaign-donut-chart', CampaignDonutChart);
